import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, TrophyIcon, ThumbsUpIcon } from 'lucide-react';
import ArticleCard from '../Articles/ArticleCard';
import { TroveActivityFeed } from './TroveActivityFeed';
import { ActivityPost } from './ActivityPostCard';
import { SearchWithAutocomplete } from '../UI/SearchWithAutocomplete';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { TroveLayout } from './TroveLayout';
import { FilterSection } from '../UI/FilterSidebar';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
// ─── Types ────────────────────────────────────────────────────────────────────
export interface TroveArticle {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
  type?: string;
}
export interface TroveEvent {
  name: string;
  date: string;
  location: string;
}
export interface TroveNominee {
  id: number;
  name: string;
  author: string;
  votes: number;
}
export interface TroveGenrePageProps {
  /** Page heading */
  title: string;
  /** Subtitle / description below the heading */
  subtitle: string;
  /** Icon rendered next to the title */
  icon: React.ReactNode;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Activity feed posts */
  activityPosts: ActivityPost[];
  /** Articles tab content */
  articles: TroveArticle[];
  /** Interviews tab content */
  interviews: TroveArticle[];
  /** Podcasts tab content */
  podcasts: TroveArticle[];
  /** Upcoming events list — omit to hide the section */
  upcomingEvents?: TroveEvent[];
  /** Award nominees list — omit to hide the section */
  awardNominees?: TroveNominee[];
  /** "View all articles" link target */
  articlesHref?: string;
  /** Title for the featured article card (default: "Featured Article") */
  featuredSectionTitle?: string;
  /** Label for the interviews tab (default: "Author Interviews") */
  interviewsTabLabel?: string;
  /** Label for the podcasts tab (default: "Podcasts") */
  podcastsTabLabel?: string;
  /** Title for the events section (default: "Upcoming Events") */
  eventsSectionTitle?: string;
  /** Link label for the events section (default: "View all events") */
  eventsLinkLabel?: string;
  /** Link href for the events section (default: "/events") */
  eventsLinkHref?: string;
  /** Activity feed section title (default: "Latest Activity") */
  activityTitle?: string;
}
// ─── Shared sub-components ────────────────────────────────────────────────────
const EmptyState = ({
  term,
  textMuted
}: {
  term: string;
  textMuted: string;
}) => <div className="text-center py-12" data-id="element-3452" data-mp="ocwvjhp6cc9s">
    <p className={textMuted} data-id="element-3453" data-mp="odydm64lxz10">No results found for "{term}"</p>
  </div>;
// ─── Component ────────────────────────────────────────────────────────────────
export const TroveGenrePage: React.FC<TroveGenrePageProps> = ({
  title,
  subtitle,
  icon,
  searchPlaceholder = 'Search content...',
  activityPosts,
  articles,
  interviews,
  podcasts,
  upcomingEvents,
  awardNominees,
  articlesHref = '/trove/articles',
  featuredSectionTitle = 'Featured Article',
  interviewsTabLabel = 'Author Interviews',
  podcastsTabLabel = 'Podcasts',
  eventsSectionTitle = 'Upcoming Events',
  eventsLinkLabel = 'View all events',
  eventsLinkHref = '/events',
  activityTitle = 'Latest Activity'
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('articles');
  const filter = (items: TroveArticle[]) => items.filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredArticles = filter(articles);
  const filteredInterviews = filter(interviews);
  const filteredPodcasts = filter(podcasts);
  const filterSections: FilterSection[] = [{
    id: 'content-type',
    title: 'Content Type',
    type: 'radio',
    value: activeTab,
    onChange: val => setActiveTab(val),
    options: [{
      value: 'articles',
      label: 'Articles'
    }, ...(interviews.length > 0 ? [{
      value: 'interviews',
      label: interviewsTabLabel
    }] : []), ...(podcasts.length > 0 ? [{
      value: 'podcasts',
      label: podcastsTabLabel
    }] : [])]
  }];
  return <TroveLayout filterSections={filterSections} data-id="element-3454" data-mp="2k7y2d69zzxcv">
      {/* Header */}
      <div className="mb-8" data-id="element-3455" data-mp="2p7svzhl5r11m">
        <div className="flex items-center gap-3 mb-2" data-id="element-3456" data-mp="2p7ketdi059km">
          <span className={rt.textPrimary} data-id="element-3457" data-mp="2u0vodme784g8">{icon}</span>
          <h1 className={`text-3xl font-bold ${dk.textPrimary}`} data-id="element-3458" data-mp="1wkzp0hh6d5j6">{title}</h1>
        </div>
        <p className={`text-base mb-6 ${dk.textMuted}`} data-id="element-3459" data-mp="21jt58hnujan1">{subtitle}</p>
        <div className="max-w-md" data-id="element-3460" data-mp="21hyj500u9chd">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder={searchPlaceholder} variant="pill" suggestions={[]} data-id="element-3461" data-mp="1u00bl127wbgs" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-3462" data-mp="1k0sy8dvhggto">
        {/* Section 1: Activity Feed */}
        <TroveActivityFeed posts={activityPosts} title={activityTitle} showTypeFilter={true} data-id="element-3463" data-mp="1f3ssk42jpg33" />

        <div className={`border-b ${dk.divider}`} data-id="element-3464" data-mp="1b2z2p55jlr7k" />

        {/* Section 2: Featured Article */}
        <section data-id="element-3465" data-mp="1694p8jgj68gx">
          <div className="flex items-center justify-between mb-4" data-id="element-3466" data-mp="168migsw8sbur">
            <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-3467" data-mp="11ce8dtwwj8ug">
              {featuredSectionTitle}
            </h2>
            {articlesHref && <Link to={articlesHref} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-3468" data-mp="rd6wzc9q9o9z">
                View all articles
              </Link>}
          </div>
          <article className={`${dk.cardSubtle} rounded-lg shadow-sm border overflow-hidden`} data-id="element-3469" data-mp="cqgkdeqk0993">
            <div className="md:flex" data-id="element-3470" data-mp="hqm5sd68cziq">
              <div className="md:w-2/5" data-id="element-3471" data-mp="hngfwvps1tly">
                <img src={articles[0]?.image} alt={articles[0]?.title} className="w-full h-48 md:h-full object-cover" data-id="element-3472" data-mp="3qtjcu1ql5gk" />
              </div>
              <div className="md:w-3/5 p-6" data-id="element-3473" data-mp="wfs3txtc0jp1">
                <div className="flex items-center mb-3" data-id="element-3474" data-mp="wh361t74pje1">
                  <span className={`px-2.5 py-0.5 text-white text-xs font-medium rounded-full ${rt.badgeBg}`} data-id="element-3475" data-mp="10f0v8w0wg5hf">
                    {articles[0]?.category}
                  </span>
                  <span className={`ml-3 text-xs ${dk.textMuted}`} data-id="element-3476" data-mp="rm5zcsxbnm4b">
                    {articles[0]?.date}
                  </span>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${dk.textPrimary}`} data-id="element-3477" data-mp="1k4thq0kxon1p">
                  {articles[0]?.title}
                </h3>
                <p className={`text-sm mb-4 line-clamp-3 ${dk.textSecondary}`} data-id="element-3478" data-mp="1thk3me64y1ns">
                  {articles[0]?.excerpt}
                </p>
                <div className="flex items-center justify-between" data-id="element-3479" data-mp="238wv34o0upta">
                  <div className="text-xs" data-id="element-3480" data-mp="2384z3sqiiq2h">
                    <p className={`font-medium ${dk.textHeading}`} data-id="element-3481" data-mp="1y9blm0b86z2b">
                      {articles[0]?.author}
                    </p>
                    <p className={dk.textMuted} data-id="element-3482" data-mp="1al0tpmnic9q6">{articles[0]?.readTime}</p>
                  </div>
                  <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-3483" data-mp="16jyyri0miju0">
                    Read full article →
                  </button>
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* Section 3: Upcoming Events (optional) */}
        {upcomingEvents && upcomingEvents.length > 0 && <>
            <div className={`border-b ${dk.divider}`} data-id="element-3484" data-mp="2qz0okj7hx96k" />
            <section data-id="element-3485" data-mp="2r0ljwb1mww5h">
              <div className="flex items-center justify-between mb-4" data-id="element-3486" data-mp="p9bobckipd9p">
                <div className="flex items-center" data-id="element-3487" data-mp="p78lfry9npnk">
                  <CalendarIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-3488" data-mp="ta5b22m84m10" />
                  <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-3489" data-mp="y9zxntyyupg9">
                    {eventsSectionTitle}
                  </h2>
                </div>
                <Link to={eventsLinkHref} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-3490" data-mp="13217l98nw434">
                  {eventsLinkLabel}
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3491" data-mp="1d0qp30wnbmiu">
                {upcomingEvents.map((event, index) => <div key={index} className={`${dk.card} border rounded-lg p-4 flex items-center justify-between`} data-id="element-3492" data-mp="1m040hz4byl67" data-mpi={index}>
                    <div data-id="element-3493" data-mp="3nqz43d4s3onh">
                      <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-3494" data-mp="3nq5snyuvkaz0">
                        {event.name}
                      </h3>
                      <p className={`text-sm ${dk.textMuted}`} data-id="element-3495" data-mp="3g8r31glwlycg">
                        {event.date} · {event.location}
                      </p>
                    </div>
                    <button className={`px-3 py-1.5 text-sm font-medium rounded-md ${rt.voteButton}`} data-id="element-3496" data-mp="36iobzlyceugr">
                      Save Event
                    </button>
                  </div>)}
              </div>
            </section>
          </>}

        {/* Section 4: Award Highlights (optional) */}
        {awardNominees && awardNominees.length > 0 && <>
            <div className={`border-b ${dk.divider}`} data-id="element-3497" data-mp="3pa7dtpgms2h7" />
            <section data-id="element-3498" data-mp="3l5hj43xpktxv">
              <div className="flex items-center justify-between mb-4" data-id="element-3499" data-mp="3l5zmrlmpaaft">
                <div className="flex items-center" data-id="element-3500" data-mp="2dt7litua9nn6">
                  <TrophyIcon className="h-5 w-5 text-amber-500 mr-2" data-id="element-3501" data-mp="2dqmk25xwd9py" />
                  <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-3502" data-mp="29psxbfw6y0yn">
                    Award Highlights
                  </h2>
                </div>
                <Link to="/events/awards" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-3503" data-mp="2z33hfno7kp72">
                  View all nominees
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3504" data-mp="382ghxqucu3fn">
                {awardNominees.map((nominee, index) => <div key={nominee.id} className={`${dk.card} border rounded-lg p-4 flex items-center justify-between`} data-id="element-3505" data-mp="3mu8xo3y29jhw" data-mpi={index}>
                    <div className="flex items-center" data-id="element-3506" data-mp="3vsav69il5u0f">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 text-xs font-bold ${index === 0 ? 'bg-amber-100 text-amber-800' : dk.badgeNeutral}`} data-id="element-3507" data-mp="3qt809hwbl466">
                        {index + 1}
                      </span>
                      <div data-id="element-3508" data-mp="bx5yi0aipjww">
                        <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-3509" data-mp="byz73l4oye7j">
                          {nominee.name}
                        </h3>
                        <p className={`text-sm ${dk.textMuted}`} data-id="element-3510" data-mp="gyljjscp3qfa">
                          by {nominee.author}
                        </p>
                      </div>
                    </div>
                    <button className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center ${rt.voteButton}`} data-id="element-3511" data-mp="74v1p6b2qe8k">
                      <ThumbsUpIcon className="h-3 w-3 mr-1" data-id="element-3512" data-mp="3ucgicpwmox6r" />
                      {rt.isReader ? 'Vote' : 'Submit'}
                    </button>
                  </div>)}
              </div>
            </section>
          </>}

        <div className={`border-b ${dk.divider}`} data-id="element-3513" data-mp="1ekj6q320sjxk" />

        {/* Section 5: Articles & Resources */}
        <section data-id="element-3514" data-mp="1im4kdqmdp28t">
          <h2 className={`text-xl font-semibold mb-6 ${dk.textPrimary}`} data-id="element-3515" data-mp="1immjcv0fdukf">
            Articles & Resources
          </h2>
          {interviews.length === 0 && podcasts.length === 0 ?
        // No tabs needed — just show articles grid directly
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3516" data-mp="2mm2rslb0gf1k">
              {filteredArticles.map(a => <ArticleCard key={a.id} article={a} data-id="element-3517" data-mp="2djjsucacimkl" />)}
              {filteredArticles.length === 0 && <div className="col-span-full" data-id="element-3518" data-mp="3a9srdubjjs8v">
                  <EmptyState term={searchQuery} textMuted={dk.textMuted} data-id="element-3519" data-mp="3abn9z1zcb89s" />
                </div>}
            </div> : <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-3520" data-mp="31gfqa53bpkbp">
              <TabsList className="mb-6" data-id="element-3521" data-mp="2whw2s09300h8">
                <TabsTrigger value="articles" data-id="element-3522" data-mp="3txa0ft0obsyz">Articles</TabsTrigger>
                {interviews.length > 0 && <TabsTrigger value="interviews" data-id="element-3523" data-mp="3p6jqs3l7t3yh">
                    {interviewsTabLabel}
                  </TabsTrigger>}
                {podcasts.length > 0 && <TabsTrigger value="podcasts" data-id="element-3524" data-mp="5b55ob7r2urd">{podcastsTabLabel}</TabsTrigger>}
              </TabsList>

              <TabsContent value="articles" className="mt-0" data-id="element-3525" data-mp="a9ossey2yclb">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3526" data-mp="ecd3yta9nvlg">
                  {filteredArticles.map(a => <ArticleCard key={a.id} article={a} data-id="element-3527" data-mp="jaeolincc2z1" />)}
                </div>
                {filteredArticles.length === 0 && <EmptyState term={searchQuery} textMuted={dk.textMuted} data-id="element-3528" data-mp="t199aikxuuid" />}
              </TabsContent>

              {interviews.length > 0 && <TabsContent value="interviews" className="mt-0" data-id="element-3529" data-mp="xzsycrutwmey">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3530" data-mp="122p95we4ne8c">
                    {filteredInterviews.map(i => <ArticleCard key={i.id} article={i} data-id="element-3531" data-mp="16yxtrpb3fzmw" />)}
                  </div>
                  {filteredInterviews.length === 0 && <EmptyState term={searchQuery} textMuted={dk.textMuted} data-id="element-3532" data-mp="pbnbta66lyiv" />}
                </TabsContent>}

              {podcasts.length > 0 && <TabsContent value="podcasts" className="mt-0" data-id="element-3533" data-mp="y4qjf1u5bh5l">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3534" data-mp="y7csj5woq4px">
                    {filteredPodcasts.map(p => <ArticleCard key={p.id} article={p} data-id="element-3535" data-mp="6evuh6a79thg" />)}
                  </div>
                  {filteredPodcasts.length === 0 && <EmptyState term={searchQuery} textMuted={dk.textMuted} data-id="element-3536" data-mp="feik6utkhauo" />}
                </TabsContent>}
            </Tabs>}
        </section>
      </div>
    </TroveLayout>;
};